/**
 * FILMS — the marketing films a company sells as Photology platform products
 * (founder, 4 Oct 2026):
 *
 *   sky_to_door — the drone film that starts high above the neighbourhood
 *                 and drops to the front door, in 16:9 and 9:16. Unlocked by
 *                 drone work on the order.
 *   layout      — The Layout, the vertical video where the floor plan leads
 *                 buyers room by room. Unlocked by a floor plan.
 *
 * Both are made AFTER the shoot, outside Photology, and come back to the
 * order as deliverables. Whichever way one was bought (inside a package, as
 * an add-on with the order, or after delivery), the founder's rules are the
 * same:
 *
 *   - it is CHARGED WHEN THE FINISHED FILM LANDS on the order, as its own
 *     card charge carrying the platform share, so a film that cannot be made
 *     is never charged and a refunded film gives the share back;
 *   - it is taxable, like every other video line;
 *   - a declined charge holds back only that film ("update your card"),
 *     never the rest of the order.
 *
 * THE KIND IS A FLAG, NOT A NAME. The company names and prices its own
 * product; `platform_film` on the catalog product says which film it is, and
 * is set from the platform product's capability when the company opts in
 * (.server/platform/materialize.ts). Nothing matches on a name or a price.
 *
 * Client-safe: types and constants only.
 */

export const FILM_KINDS = ['sky_to_door', 'layout'] as const;

export type FilmKind = (typeof FILM_KINDS)[number];

export function isFilmKind(value: unknown): value is FilmKind {
  return typeof value === 'string' && (FILM_KINDS as readonly string[]).includes(value);
}

/**
 * The platform-catalog capability each film is sold under. Deliberately NOT
 * `marketing_video`: an opted-in resold `marketing_video` product is flagged
 * as THE video bundle (materialize.ts), and the bundle offer sells the first
 * flagged product it finds, so a film on that capability could be sold as the
 * bundle.
 */
export const FILM_CAPABILITY: Record<FilmKind, string> = {
  sky_to_door: 'sky_to_door_film',
  layout: 'layout_video',
};

/** The film a platform product's capability makes, or undefined. */
export function filmKindOfCapability(capability: string | undefined): FilmKind | undefined {
  return FILM_KINDS.find((kind) => FILM_CAPABILITY[kind] === capability);
}

/**
 * What we call it where the company's own product name is not to hand
 * (office screens, logs). Customer-facing copy uses the company's product
 * name. Neither ever names who makes the film.
 */
export const FILM_LABEL: Record<FilmKind, string> = {
  sky_to_door: 'Sky-to-Door film',
  layout: 'The Layout',
};

/**
 * How it was bought. A film bought WITH THE ORDER (an add-on, or inside a
 * package; `with_order`) is a line on the order's invoice, charged with the
 * order and refunded if it cannot be made or never arrives (founder, 5 Oct
 * 2026: option A). A film bought after delivery is charged on its own payin
 * when it lands (film-charge.ts).
 *   order    — an add-on, chosen with the shoot
 *   delivery — bought after delivery (deliverables page, delivery email)
 *   package  — inside a package whose price already includes it
 */
export const FILM_SOURCES = ['order', 'delivery', 'package'] as const;

export type FilmSource = (typeof FILM_SOURCES)[number];

/**
 * Where one film is.
 *   ordered   — owed; waiting to be made, or made and waiting to be charged
 *   charging  — the claim a charge holds while it settles (film-charge.ts)
 *   paid      — charged, on its own payin
 *   invoiced  — a monthly-invoice customer: on the invoice, billed with the month
 *   declined  — the card would not pay; only this film is held back
 *   failed    — it could not be made; never charged
 *   cancelled — taken off the order before it was made; never charged
 *   refunding — a with-the-order film's refund is being made (a claim)
 *   refunded  — a with-the-order film that could not be made, or never came:
 *               its line was refunded off the order's payment
 */
export const FILM_STATUSES = ['ordered', 'charging', 'paid', 'invoiced', 'declined', 'failed', 'cancelled', 'refunding', 'refunded'] as const;

export type FilmStatus = (typeof FILM_STATUSES)[number];

/**
 * One film on an order: `order.data.films[kind]`. A record, not a job: a job
 * on a delivered order would reopen it (PAID -> STARTED) and re-run delivery,
 * which is why the reel, the video bundle and the post-delivery floor plan
 * are stamps too.
 */
export interface OrderFilm {
  status: FilmStatus;
  source: FilmSource;
  ordered_at: TDateISO;
  /** The company's catalog product it was sold as. */
  catalog_product_id?: string;
  /** The product's name and pre-tax price AS SOLD, so a later reprice never changes what they agreed to. */
  name?: string;
  price?: string;
  /** Photology's share of `price`, minor units, at the company's rate when sold. */
  platform_minor?: number;
  /** Time of the last claim or status change; a `charging` claim older than the stale window died mid-flight. */
  at?: TDateISO;
  /** The invoice line the charge added. Removed again when the card is refused. */
  line_id?: string;
  payin_id?: string;
  /** What the card was charged, tax included. */
  amount?: string;
  charged_at?: TDateISO;
  /**
   * The idempotency key of the charge in progress (film-charge.ts), kept on
   * the record so an unclear outcome (an error, a worker that died) retries
   * under the SAME key and Rainforest hands back the original payin instead
   * of charging again. Rotated only after a definite refusal.
   */
  charge_key?: string;
  /**
   * What was sent under `charge_key`, written before the charge: the amount
   * and the card. A retry under the same key that would charge a different
   * amount or card goes to the office instead (Rainforest would hand back
   * the original payin, not the new charge).
   */
  charge_amount?: string;
  charge_card_id?: string;
  /** Unclear outcomes so far for this key, and the last one's message. */
  charge_errors?: number;
  charge_error?: string;
  /** The office must look (repeated unclear outcomes, an amount that does not add up): no more automatic tries. */
  needs_office_at?: TDateISO;
  /**
   * Why the charge went to the office: `unclear` (repeated errors: a retry
   * under the SAME key is safe, Rainforest hands back the original payin),
   * `amount_mismatch` / `key_mismatch` (what the key was sent with no longer
   * matches: a re-armed charge needs a new key), `processor_error`
   * (Rainforest refused the charge request, not the card: nothing was
   * charged, and a re-armed charge needs a new key).
   */
  office_reason?: 'unclear' | 'amount_mismatch' | 'key_mismatch' | 'processor_error';
  /** The office was alerted that this film's `charging` claim holds the order's balance charge. */
  office_alerted_at?: TDateISO;
  /** Every card that refused this film, so a retry skips them all and waits for a different one. Cleared only on success. */
  declined_card_ids?: string[];
  declined_reason?: string;
  declined_at?: TDateISO;
  failed_reason?: string;
  failed_at?: TDateISO;
  /** When the finished film landed on the order (set by the import). Nothing is charged before this. */
  delivered_at?: TDateISO;
  /** The job the film's deliverables sit on: the drone job, or the job with the floor plan. */
  host_job_id?: string;
  /** The order files the film arrived as, one per shape (16:9, 9:16). */
  file_ids?: string[];
  /** "Your film is ready" was sent. */
  ready_notified_at?: TDateISO;
  /** "Your film is ready, pay to unlock" went out while the order was unpaid (unpaid-delivery.ts); "ready" itself still follows the payment, once. */
  ready_unpaid_notified_at?: TDateISO;
  /**
   * Bought WITH THE ORDER: `line_id` is its line on the order's invoice,
   * charged with the order (the order's charge carries its platform share,
   * film-order.ts), never on a payin of its own.
   */
  with_order?: boolean;
  /** When the order's own media went out: what "late" and the automatic refund count from. */
  media_ready_at?: TDateISO;
  /** The office was told the film is late. */
  late_alerted_at?: TDateISO;
  /** The share was not split off the order's charge (it captured a card hold, which takes no split). */
  split_missing?: boolean;
  /** Refunded: why, and what (the line with its tax). */
  refunded_at?: TDateISO;
  refund_reason?: 'failed' | 'late';
  refund_amount?: string;
  /** Photology's share, NOT returned by a partial refund (Rainforest reverses a split only on a full refund). */
  platform_minor_unreturned?: number;
  /** "Couldn't be made, refunded" was sent. */
  refunded_notified_at?: TDateISO;
  /**
   * A refund Rainforest refused or could not make (a payin still processing,
   * a payment made outside Rainforest): tries so far, the last reason, and
   * when the next try is due. The office is alerted on the first.
   */
  refund_attempts?: number;
  refund_error?: string;
  refund_next_at?: TDateISO;
  /**
   * How many refunds the payment already had when this film's refund was
   * claimed. Rainforest's refund key counts them, so while the payment still
   * has this many, "Finish refund" replays the SAME refund (never a second
   * one) and records it (film-retry.ts finishFilmRefund).
   */
  refund_seq?: number;
  /** The office refunded it by hand and marked it refunded (film-retry.ts markFilmRefunded). */
  refund_by_hand?: boolean;
  /** Paid outside the order's balance charge (an office charge, a recorded payment): no split was taken. */
  paid_outside?: boolean;
  /** "Your card was declined for this film" was sent, for the current decline. */
  declined_notified_at?: TDateISO;
  /**
   * Paid inside a package whose price already covers it (the Sky-to-Door
   * Tour's film, The Layout in a video bundle): `paid` with no charge of its
   * own. Released like any paid film.
   */
  included?: boolean;
  /** The finished files, as the maker last announced them: what makes a repeated announcement a no-op, and what a lost import is queued again from. */
  import_files?: Array<{ aspect: 'landscape' | 'portrait'; url: string }>;
  /** When an import of those files was queued, and when one last failed (and why). */
  import_requested_at?: TDateISO;
  import_failed_at?: TDateISO;
  import_error?: string;
  /** When the film's deliverables were put in front of the customer (paid, or included). */
  released_at?: TDateISO;
  /** When the order was handed to the maker with this film owed (the automatic hand-off). */
  handed_off_at?: TDateISO;
  /** What Bleenk was last told of this film's payment (.server/films/film-payment.ts): each change is sent once. */
  bleenk_payment?: 'paid' | 'invoiced' | 'unpaid';
}

/** The shapes each film is delivered in. */
export const FILM_ASPECTS: Record<FilmKind, ReadonlyArray<'landscape' | 'portrait'>> = {
  sky_to_door: ['landscape', 'portrait'],
  layout: ['portrait'],
};

/** What the customer is promised about when (founder, 4 Oct 2026: the Sky-to-Door film within 3 business days). */
export const FILM_PROMISE: Record<FilmKind, string> = {
  sky_to_door: 'within 3 business days',
  layout: 'once the floor plan is in',
};

/** A film still owed to the customer: on the order, not failed, not taken off. */
export function filmOwed(film: OrderFilm | undefined): film is OrderFilm {
  return Boolean(film) && !['failed', 'cancelled', 'refunding', 'refunded'].includes(film!.status);
}

export type OrderFilms = Partial<Record<FilmKind, OrderFilm>>;

/** A `refunding` claim older than this died mid-way (film-order.ts refundOrderFilm). */
export const FILM_REFUNDING_STALE_MS = 15 * 60 * 1000;

/** A refund claim that died mid-way: the money may or may not have gone back. */
export function filmRefundStuck(film: Pick<OrderFilm, 'status' | 'at'> | undefined, now = Date.now()): boolean {
  return film?.status === 'refunding' && Boolean(film.at) && now - Date.parse(film.at!) >= FILM_REFUNDING_STALE_MS;
}

/** One film, as the order's Deliverables page shows it (.server/films/film-panel.ts, FilmCard). */
export interface FilmPanelEntry {
  kind: FilmKind;
  name: string;
  state: 'preparing' | 'finishing' | 'declined' | 'failed' | 'refunded' | 'ready';
  /** For `preparing`: when it is promised, in words. */
  promise?: string;
  /** For `failed`: why, in the customer's words. */
  reason?: string;
  /** Office only: what the office should know (an import that failed, a charge still owed). */
  office_note?: string;
  /** Office only: the import gave up, and "Retry film" may queue it again. */
  retry?: boolean;
  /** Office only: its charge was left for the office, and "Charge again" re-arms it. */
  rearm?: boolean;
  /** Office only: its refund failed; the office refunded it in Accounting, and "Mark refunded" closes the film. */
  mark_refunded?: boolean;
  /** Office only: a refund that died mid-way; "Finish refund" replays the same refund and records it. */
  finish_refund?: boolean;
}
